import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";

import { getCookies } from "cookies-next";
import { useTranslation } from "next-i18next";
import { ModalContext } from "@/pages/_app";

import useOnClickOutside from "@/hooks/useOnClickOutside";
import useFreshCart from "@/hooks/useFreshCart";

import Star from "../../icons/Star";

import ProductFees from "./ProductFees";
import Info from "./Info";
import FrequentlyBoughtTogetherSwiper from "./FrequentlyBoughtTogetherSwiper";
import LastSection from "./LastSection";
import BannerSection from "./BannerSection";
import CloseCircle from "@/components/icons/close-circle";
import EWSlots from "@/components/ExtendedWarranty/EWSlots";

// Per-line prices the drawer shows: the tax-inclusive selling price and the
// tax-inclusive original for the struck-through value — matching the PDP.
const getLinePrice = (item) => {
  // ES shape (freshly-added product) already carries tax-inclusive prices.
  const es = item?.prices_with_tax;
  if (es) {
    return {
      selling: +(es.discounted_price || es.price) || null,
      original: +es.original_price || null,
    };
  }
  // Magento cart line: price_incl_tax is the tax-inclusive selling price;
  // extension_attributes.original_price is ex-tax, so scale it by the line's tax
  // ratio to keep the struck value on the same basis as the PDP.
  const inclTax = +item?.price_incl_tax || null;
  const exTax = +item?.price || null;
  const ratio = inclTax && exTax ? inclTax / exTax : 1;
  const original = +item?.extension_attributes?.original_price || null;
  return { selling: inclTax, original: original ? original * ratio : null };
};

function CartInfo({ Context }) {
  const { t } = useTranslation("common");
  const { region_id } = getCookies();
  const { push, locale } = useRouter();
  const cartModel = useContext(ModalContext);

  // Refetch the Magento-fresh cart on open and after every add; `cartFresh`
  // flips once it lands so we show a loading state instead of a stale value.
  const { cart, fresh: cartFresh } = useFreshCart();

  const cartInfoRef = useRef(null);
  useOnClickOutside([cartInfoRef], () =>
    Context?.dispatch({ type: "closeModal", payload: "" })
  );

  // Lock page scroll while open; close on Escape. `overflow: hidden` alone is
  // unreliable on iOS — the document scrolls on the viewport, not body, and
  // momentum keeps going after an SPA navigation. So pin the page with the same
  // `position: fixed` + saved scrollY pattern the SearchBox and category filters
  // already use, which is the only thing that reliably stops iOS scrolling.
  useEffect(() => {
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        Context?.dispatch({ type: "closeModal", payload: "" });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
      document.removeEventListener("keydown", onKey);
    };
  }, [Context]);

  // Height strategy — dvh-first, JS only as a fallback: modern browsers support
  // `100dvh`, which tracks the browser toolbar natively with no JS. We only
  // measure the visible height for legacy webviews that don't understand dvh.
  const supportsDvh = window?.CSS?.supports?.("height: 100dvh");
  const [visibleHeight, setVisibleHeight] = useState(null);
  useEffect(() => {
    if (supportsDvh) return undefined;
    // visualViewport: window.innerHeight reports the layout viewport (behind
    // the collapsed toolbar) and its resize event doesn't fire on URL-bar
    // change, so for legacy webviews we measure the visible area instead.
    const sync = () =>
      setVisibleHeight(window.visualViewport?.height ?? window.innerHeight);

    sync();
    window.visualViewport?.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("scroll", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      window.visualViewport?.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("scroll", sync);
      window.removeEventListener("orientationchange", sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [
    frequentlyBoughtTogetherProducts,
    setFrequentlyBoughtTogetherProducts,
  ] = useState(cartModel?.state.relatedProducts);
  useEffect(() => {
    setFrequentlyBoughtTogetherProducts(cartModel?.state.relatedProducts);
  }, [cartModel?.state.relatedProducts]);

  // Total = subtotal (incl VAT) + EW, straight from GET_CART. Rendered only once
  // the fresh cart lands (skeleton until then) so it never flashes a stale value.
  const ewSegment =
    cart?.cart?.total_segments?.find((s) => s?.code === "extended_warranty")
      ?.value || 0;
  const totalPrice = (+cart?.cart?.subtotal_incl_tax || 0) + ewSegment;

  return (
    // dvh/vh only as pre-hydration default; measured height overrides below.
    <div
      className="fixed top-0 left-0 z-[5000] w-full h-screen supports-[height:100dvh]:h-[100dvh] bg-lightblack overflow-hidden"
      style={visibleHeight ? { height: visibleHeight } : undefined}
    >
      <div
        ref={cartInfoRef}
        className="absolute top-0 left-0 flex flex-col w-full h-full bg-white md:w-[455px] rounded-r-2xl rounded-br-xl"
      >
        <div className="flex items-center justify-between p-4 bg-white rounded-r-2xl shrink-0">
          <div className="flex items-center">
            <Star />
            <h4 className="mx-2 text-lg font-semibold">{t("GreatChoice")}</h4>
          </div>
          <CloseCircle
            onClick={() =>
              Context?.dispatch({ type: "closeModal", payload: "" })
            }
            size="20px"
            className="cursor-pointer"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto gray-scrollbarV2">
          <div className="flex flex-col gap-2.5 px-4 my-4">
            {cart?.cartItems?.map((item) => {
              const { selling, original } = getLinePrice(item);
              const price = {
                specialPrice: selling,
                originalPrice: original ?? selling,
                discount:
                  original && selling && original > selling
                    ? original - selling
                    : 0,
              };
              const availableProductFees = (
                item?.product_fees || item?.extension_attributes?.product_fees
              )?.filter((fee) => fee?.region_ids?.includes(region_id || "1101"));

              return (
                <div
                  key={item?.item_id}
                  className="flex flex-col border-gray-100 border-b-2 pb-2 last:border-none"
                >
                  <Info {...{ item, price, loading: !cartFresh }} />
                  <EWSlots item={item} mode="mini" locale={locale} />
                  <ProductFees {...{ availableProductFees, item, locale }} />
                </div>
              );
            })}
          </div>

          <FrequentlyBoughtTogetherSwiper
            {...{ frequentlyBoughtTogetherProducts, locale, t }}
          />

          <BannerSection {...{ locale, t }} />
        </div>

        <LastSection {...{ totalPrice, Context, t, push, loading: !cartFresh }} />
      </div>
    </div>
  );
}

export default CartInfo;
